const TOKEN_STORAGE_KEY = "blackadder:api_token";
export const AUTH_TOKEN_QUERY_KEY = "authToken";
export const AUTH_TOKEN_CHANGE_EVENT = "blackadder:auth-token-change";
const COOKIE_NAME = "api_token";

const getStorage = (): Storage | null => {
	if (typeof window === "undefined") {
		return null;
	}

	try {
		return window.localStorage;
	} catch {
		return null;
	}
};

const notifyAuthTokenChanged = () => {
	if (typeof window === "undefined") {
		return;
	}

	window.dispatchEvent(new Event(AUTH_TOKEN_CHANGE_EVENT));
};

const storageGetItem = (key: string): string | null => {
	const storage = getStorage();
	if (!storage) {
		return null;
	}

	try {
		return storage.getItem(key);
	} catch {
		return null;
	}
};

const storageSetItem = (key: string, value: string) => {
	const storage = getStorage();
	if (!storage) {
		return;
	}

	try {
		storage.setItem(key, value);
	} catch {
		// noop: localStorage can fail in private mode or quota exceeded.
	}
};

const storageRemoveItem = (key: string) => {
	const storage = getStorage();
	if (!storage) {
		return;
	}

	try {
		storage.removeItem(key);
	} catch {
		// noop
	}
};

const readTokenFromCookie = (): string | null => {
	if (typeof document === "undefined") {
		return null;
	}

	const apiTokenCookie = document.cookie
		.split(";")
		.map((cookie) => cookie.trim())
		.find((cookie) => cookie.startsWith(`${COOKIE_NAME}=`));

	if (!apiTokenCookie) {
		return null;
	}

	const value = apiTokenCookie.slice(COOKIE_NAME.length + 1);
	if (!value) {
		return null;
	}

	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
};

const clearCookieToken = () => {
	if (typeof document === "undefined") {
		return;
	}

	const host =
		typeof window !== "undefined" ? window.location.hostname : undefined;
	const domains = new Set<string>([""]);

	if (host) {
		domains.add(host);
		if (host.endsWith(".blackadder.local") || host === "blackadder.local") {
			domains.add(".blackadder.local");
		}
		if (
			host.endsWith(".blackadder.co.jp") ||
			host === "blackadder.co.jp"
		) {
			domains.add(".blackadder.co.jp");
		}
	}

	domains.forEach((domain) => {
		const domainPart = domain ? `; domain=${domain}` : "";
		document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/${domainPart}; Secure; SameSite=None`;
		document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/${domainPart}`;
	});
};

export const getAuthToken = (): string | null => {
	const storedToken = storageGetItem(TOKEN_STORAGE_KEY);
	if (storedToken) {
		return storedToken;
	}

	const cookieToken = readTokenFromCookie();
	if (cookieToken) {
		storageSetItem(TOKEN_STORAGE_KEY, cookieToken);
	}

	return cookieToken;
};

export const storeAuthToken = (token: string) => {
	const normalizedToken = token.trim();
	if (!normalizedToken) {
		return;
	}

	storageSetItem(TOKEN_STORAGE_KEY, normalizedToken);
	notifyAuthTokenChanged();
};

export const clearAuthToken = () => {
	storageRemoveItem(TOKEN_STORAGE_KEY);
	clearCookieToken();
	notifyAuthTokenChanged();
};

export const bootstrapTokenFromUrl = (): string | null => {
	if (typeof window === "undefined") {
		return null;
	}

	try {
		const url = new URL(window.location.href);
		const token = url.searchParams.get(AUTH_TOKEN_QUERY_KEY);
		const normalizedToken = token?.trim() ?? "";

		if (!normalizedToken) {
			return null;
		}

		storeAuthToken(normalizedToken);
		url.searchParams.delete(AUTH_TOKEN_QUERY_KEY);

		const cleanUrl = `${url.pathname}${url.search}${url.hash}`;
		window.history.replaceState({}, document.title, cleanUrl);
		return normalizedToken;
	} catch {
		return null;
	}
};

export const isAuthenticated = (): boolean => {
	return !!getAuthToken();
};
